import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, MessageCircle, Bookmark, Share2, Play, Briefcase, Sparkles, MapPin, ChevronUp } from 'lucide-react';
import { SectionHeader } from './HowItWorksSection';

const reels = [
  {
    handle: '@beatriz_cafeteria',
    name: 'Cafeteria Beatriz',
    bg: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=600&q=80',
    avatarLetter: 'B',
    avatarColor: 'linear-gradient(135deg, #ffa726, #ff7043)',
    caption: 'Abri minha cafeteria há 1 ano. Quero abrir mais 2 unidades. ☕✨',
    tags: '#procurandoinvestidor #cafeteria',
    needLabel: 'Procurando Spark',
    amount: 'R$ 80.000',
    likes: 2847,
    comments: 182,
    location: 'São Paulo · SP',
  },
  {
    handle: '@daniel_marcenaria',
    name: 'Marcenaria do Dani',
    bg: 'https://images.unsplash.com/photo-1567361808960-dec9cb578182?w=600&q=80',
    avatarLetter: 'D',
    avatarColor: 'linear-gradient(135deg, #4facfe, #1e90ff)',
    caption: 'Móveis artesanais com madeira de reflorestamento. Quero virar e-commerce nacional 🌱',
    tags: '#artesanal #marcenaria',
    needLabel: 'Procurando Spark',
    amount: 'R$ 45.000',
    likes: 1923,
    comments: 134,
    location: 'Curitiba · PR',
  },
  {
    handle: '@joana_pets',
    name: 'PetSpot Express',
    bg: 'https://images.unsplash.com/photo-1450778869180-41d0601e046e?w=600&q=80',
    avatarLetter: 'J',
    avatarColor: 'linear-gradient(135deg, #ff7043, #ffa726)',
    caption: 'Delivery de produtos pet em 30min. Cresci 4x em 6 meses 🐾',
    tags: '#pet #delivery #tech',
    needLabel: 'Procurando Spark',
    amount: 'R$ 120.000',
    likes: 4521,
    comments: 312,
    location: 'Belo Horizonte · MG',
  },
];

const FeedSection = () => {
  return (
    <section id="feed" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/3 w-[600px] h-[600px] rounded-full" style={{
          background: 'radial-gradient(circle, rgba(79,172,254,0.15), transparent 70%)'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="O feed das oportunidades"
          title={<>Role o feed. Descubra <span className="gradient-text">ideias incríveis</span>.</>}
          subtitle="Cada vídeo é uma história, um sonho, uma chance. Curta, salve e clique em 'Tenho Interesse' quando encontrar a Spark certa."
        />

        <div className="mt-16 grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-center">
          {/* Left: Three phones stacked */}
          <PhonesStack />

          {/* Right: Description and features */}
          <FeedFeatures />
        </div>
      </div>
    </section>
  );
};

const PhonesStack = () => {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActiveIdx((i) => (i + 1) % reels.length), 4200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative h-[600px] sm:h-[620px] flex items-center justify-center">
      {reels.map((r, i) => {
        const offset = (i - activeIdx + reels.length) % reels.length;
        const isActive = offset === 0;
        // Lay out as a fan / stack
        let translateX = 0;
        let translateY = 0;
        let scale = 1;
        let rotateZ = 0;
        let opacity = 1;
        let zIndex = 30;
        if (offset === 1) { translateX = 130; translateY = 30; scale = 0.85; rotateZ = 6; opacity = 0.55; zIndex = 20; }
        else if (offset === 2) { translateX = -130; translateY = 30; scale = 0.85; rotateZ = -6; opacity = 0.55; zIndex = 20; }

        return (
          <motion.div
            key={i}
            animate={{
              x: translateX,
              y: translateY,
              scale,
              rotate: rotateZ,
              opacity,
              zIndex,
            }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="absolute"
            style={{ filter: isActive ? 'drop-shadow(0 30px 80px rgba(79,172,254,0.3))' : 'none' }}
          >
            <ReelPhone reel={r} active={isActive} />
          </motion.div>
        );
      })}
    </div>
  );
};

const ReelPhone = ({ reel, active }) => {
  const [liked, setLiked] = useState(false);
  return (
    <div className="relative w-[280px] sm:w-[300px]">
      <div className="absolute -inset-6 bg-gradient-to-br from-[#4facfe]/20 to-[#ffa726]/15 rounded-full blur-2xl" />
      <div className="relative bg-gradient-to-b from-[#1a1f3a] to-[#0a0e1f] rounded-[2.2rem] p-1.5 border border-white/10 shadow-2xl">
        <div className="relative bg-[#050816] rounded-[1.8rem] overflow-hidden w-full aspect-[9/19]">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-4 bg-black rounded-full z-30" />

          {/* Reel content */}
          <div className="absolute inset-0">
            <img src={reel.bg} alt={reel.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(180deg, rgba(0,0,0,0.4), transparent 30%, transparent 60%, rgba(0,0,0,0.85))'
            }} />
          </div>

          {/* Top: tab indicator */}
          <div className="absolute top-8 left-0 right-0 flex items-center justify-center gap-5 text-[11px] font-semibold z-20">
            <span className="text-white/50">Pra você</span>
            <span className="text-white relative">
              Em alta
              <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full bg-gradient-to-r from-[#4facfe] to-[#ffa726]" />
            </span>
          </div>

          {/* Center play */}
          {active && (
            <motion.div
              animate={{ scale: [1, 1.08, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2.2, repeat: Infinity }}
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center ring-2 ring-white/30">
                <Play size={16} className="text-white fill-white ml-0.5" />
              </div>
            </motion.div>
          )}

          {/* Right action rail */}
          <div className="absolute right-2.5 bottom-32 z-20 flex flex-col items-center gap-4">
            <button onClick={(e) => { e.stopPropagation(); setLiked(!liked); }} className="flex flex-col items-center">
              <div className={`w-9 h-9 rounded-full backdrop-blur-md flex items-center justify-center ${liked ? 'bg-red-500/30' : 'bg-white/10'}`}>
                <Heart size={16} className={liked ? 'fill-red-500 text-red-500' : 'text-white'} />
              </div>
              <span className="text-[9px] text-white mt-0.5 font-semibold">{(reel.likes + (liked ? 1 : 0)).toLocaleString('pt-BR')}</span>
            </button>
            <button className="flex flex-col items-center">
              <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center">
                <MessageCircle size={15} className="text-white" />
              </div>
              <span className="text-[9px] text-white mt-0.5 font-semibold">{reel.comments}</span>
            </button>
            <button className="flex flex-col items-center">
              <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center">
                <Bookmark size={15} className="text-white" />
              </div>
              <span className="text-[9px] text-white mt-0.5 font-semibold">Salvar</span>
            </button>
            <button className="flex flex-col items-center">
              <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center">
                <Share2 size={14} className="text-white" />
              </div>
            </button>
          </div>

          {/* Bottom caption */}
          <div className="absolute bottom-0 left-0 right-0 px-3 pb-3 pt-8 z-20"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)' }}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-black ring-2 ring-white"
                style={{ background: reel.avatarColor }}>
                {reel.avatarLetter}
              </div>
              <div className="text-[11px] font-semibold text-white">{reel.handle}</div>
              <div className="text-[8px] text-[#4facfe] font-medium border border-[#4facfe]/40 rounded-full px-1.5 py-0.5 bg-[#4facfe]/10">
                Seguir
              </div>
            </div>
            <p className="text-[10px] text-white leading-snug">
              {reel.caption} <span className="text-[#4facfe]">{reel.tags}</span>
            </p>
            <div className="flex items-center gap-1 mt-1 text-[9px] text-slate-300">
              <MapPin size={9} /> {reel.location}
            </div>
            {/* Spark CTA pill */}
            <motion.div
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-bold text-white"
              style={{
                background: 'linear-gradient(135deg, #4facfe, #1e90ff)',
                boxShadow: '0 0 20px rgba(79,172,254,0.5)',
              }}
            >
              <Briefcase size={9} />
              {reel.needLabel} · {reel.amount}
            </motion.div>
          </div>

          {/* Scroll hint */}
          <div className="absolute top-1/3 right-1/2 translate-x-1/2 pointer-events-none">
            {active && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: [0, 0.7, 0], y: [10, -10, -20] }}
                transition={{ duration: 2.5, repeat: Infinity, delay: 1 }}
                className="text-white/60 text-[10px] flex flex-col items-center"
              >
                <ChevronUp size={14} />
                <span>role pra ver mais</span>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FeedFeatures = () => {
  const items = [
    { icon: Heart, title: 'Curta o que te emociona', desc: 'Mostre apoio aos negócios que te tocam. Cada curtida importa.' },
    { icon: Bookmark, title: 'Salve pra depois', desc: 'Guarde as ideias que mais combinam com você pra revisitar.' },
    { icon: Sparkles, title: 'Toque em "Tenho Interesse"', desc: 'Esse é o momento. A Spark nasce e o chat com o empreendedor abre.' },
  ];
  return (
    <div className="space-y-4">
      {items.map((it, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, delay: i * 0.1 }}
          whileHover={{ x: 6 }}
          className="glass-card rounded-2xl p-5 sm:p-6 flex gap-4 spotlight-card"
        >
          <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
            i === 2
              ? 'bg-gradient-to-br from-[#ffa726]/25 to-[#ff7043]/10 text-[#ffd54f]'
              : 'bg-gradient-to-br from-[#4facfe]/25 to-[#1e90ff]/10 text-[#4facfe]'
          }`}>
            <it.icon size={20} />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white">{it.title}</h3>
            <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{it.desc}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default FeedSection;
